import React, { useState, useEffect, useCallback } from "react";
import "./Schedules.css";
import Layout from "../UI/Layout";
import {
  collection, getDocs, doc, updateDoc, addDoc, deleteDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import TimePicker24 from "../UI/TimePicker24";

const Snackbar = ({ message, type, visible }) => (
  <div className={`sch-snackbar sch-snackbar-${type} ${visible ? "sch-snackbar-show" : ""}`}>
    <i className={`fas ${type === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}></i>
    {message}
  </div>
);

/* ── Date helpers ─────────────────────────────────────── */
const getWeekBounds = (sunday) => {
  const s = new Date(sunday); s.setHours(0, 0, 0, 0);
  const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23, 59, 59, 999);
  return { start: s, end: e };
};

const getThisWeekSunday = () => {
  const now = new Date();
  const sun = new Date(now);
  sun.setDate(now.getDate() - now.getDay());
  sun.setHours(0, 0, 0, 0);
  return sun;
};

const addDays = (date, days) => {
  const d = new Date(date); d.setDate(d.getDate() + days); return d;
};

const fmtDate = (d, withYear = false) => {
  if (!d) return "—";
  const opts = { month: "short", day: "numeric" };
  if (withYear) opts.year = "numeric";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", opts);
};

const fmtWeekLabel = (sun) => {
  const sat = addDays(sun, 6);
  const opts = { month: "short", day: "numeric", year: "numeric" };
  return `${sun.toLocaleDateString("en-US", opts)} – ${sat.toLocaleDateString("en-US", opts)}`;
};

const toDateStr = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const shiftDateStr = (dateStr, days) => {
  if (!dateStr) return dateStr;
  const [y, m, day] = dateStr.split("-").map(Number);
  const d = new Date(y, m - 1, day);
  d.setDate(d.getDate() + days);
  return toDateStr(d);
};

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const SOLO_COLORS = {
  "1": { bg: "rgba(52,199,89,0.13)", color: "#27a84a", border: "rgba(52,199,89,0.35)", btnBg: "rgba(52,199,89,0.15)", btnHover: "rgba(52,199,89,0.28)" },
  "2": { bg: "rgba(10,132,255,0.12)", color: "#0a84ff", border: "rgba(10,132,255,0.35)", btnBg: "rgba(10,132,255,0.13)", btnHover: "rgba(10,132,255,0.26)" },
  "5": { bg: "rgba(255,159,10,0.13)", color: "#d4820a", border: "rgba(255,159,10,0.35)", btnBg: "rgba(255,159,10,0.14)", btnHover: "rgba(255,159,10,0.28)" },
};

const soloCopyBtnStyle = (soloKey, isHovered = false) => {
  const c = SOLO_COLORS[soloKey];
  if (!c) return {};
  return {
    background: isHovered ? c.btnHover : c.btnBg,
    color: c.color,
    border: `1px solid ${c.border}`,
    transition: "background 0.18s ease",
  };
};

const COPY_ALL_STYLE = {
  base: { background: "rgba(120,86,255,0.13)", color: "#7856ff", border: "1px solid rgba(120,86,255,0.35)", transition: "background 0.18s ease" },
  hover: { background: "rgba(120,86,255,0.25)", color: "#7856ff", border: "1px solid rgba(120,86,255,0.45)", transition: "background 0.18s ease" },
};

/* Format time from "HH:MM" to "h:MM AM/PM" */
const fmtTime = (t) => {
  if (!t) return null;
  const [hStr, mStr] = t.split(":");
  return `${hStr}:${mStr || "00"}`;
};

/* ── Day cell content logic ───────────────────────────────
   For each block that covers this dateStr, determine what to show:
   - Solo 1  : always single-day; show startTime only
   - Solo 2/5: multi-day span; show startTime on startDate cell,
               END (no time) on endDate cell, a "continues" bar on middle days
──────────────────────────────────────────────────────────── */
const getCellContent = (schedule, block, dateStr) => {
  const isSolo1 = schedule.solo === "1";
  const startDate = block.startDate || "";
  const endDate = block.endDate || block.startDate || "";

  if (isSolo1) {
    // Solo 1: single-day trip — always show start time
    return { type: "solo1-start", time: fmtTime(block.startTime) };
  }

  // Solo 2 / 5: multi-day trip
  const isStart = dateStr === startDate;
  const isEnd = dateStr === endDate;
  const isMiddle = !isStart && !isEnd;

  if (isStart) return { type: "range-start", time: fmtTime(block.startTime) };
  if (isEnd) return { type: "range-end" };
  if (isMiddle) return { type: "range-mid" };

  return { type: "empty" };
};

const timeToMinutes = (t) => {
  if (!t) return null;
  const [hStr, mStr] = String(t).split(":");
  const h = Number(hStr);
  const m = Number(mStr || 0);
  if (Number.isNaN(h)) return null;
  return h * 60 + (Number.isNaN(m) ? 0 : m);
};

const getDayTimeMinutes = (schedule, dateStr) => {
  const blocks = (schedule.blocks || []).filter((b) => {
    if (!b.startDate) return false;
    const end = b.endDate || b.startDate;
    return dateStr >= b.startDate && dateStr <= end;
  });
  let earliest = null;
  for (const block of blocks) {
    const cell = getCellContent(schedule, block, dateStr);
    if (!cell.time) continue;
    const mins = timeToMinutes(block.startTime);
    if (mins == null) continue;
    if (earliest == null || mins < earliest) earliest = mins;
  }
  return earliest;
};

const compareByDriverName = (a, b) =>
  (a.driverName || "").localeCompare(b.driverName || "", undefined, { sensitivity: "base" });

const getEarliestWeekTimeMinutes = (schedule, weekDays) => {
  let earliest = null;
  for (const { dateStr } of weekDays) {
    const mins = getDayTimeMinutes(schedule, dateStr);
    if (mins == null) continue;
    if (earliest == null || mins < earliest) earliest = mins;
  }
  return earliest;
};

const compareByWeekTimes = (a, b, weekDays) => {
  const ta = getEarliestWeekTimeMinutes(a, weekDays);
  const tb = getEarliestWeekTimeMinutes(b, weekDays);
  if (ta == null && tb == null) return compareByDriverName(a, b);
  if (ta == null) return 1;
  if (tb == null) return -1;
  if (ta !== tb) return ta - tb;
  return compareByDriverName(a, b);
};

const Schedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [copyingAll, setCopyingAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [soloFilter, setSoloFilter] = useState("");
  const [filterMode, setFilterMode] = useState("this_week");
  const [weekSunday, setWeekSunday] = useState(getThisWeekSunday());
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ message: "", type: "success", visible: false });
  const [copyBtnHover, setCopyBtnHover] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [sortByDriver, setSortByDriver] = useState(false);

  const showSnackbar = useCallback((message, type = "success") => {
    setSnackbar({ message, type, visible: true });
    setTimeout(() => setSnackbar((s) => ({ ...s, visible: false })), 3500);
  }, []);

  /* ── Fetch schedules ── */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "schedules"));
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) =>
            (a.driverName || "").localeCompare(b.driverName || "", undefined, { sensitivity: "base" })
          );
        setSchedules(data);
      } catch (e) {
        console.error("Error fetching schedules:", e);
        showSnackbar("Failed to load schedules.", "error");
      }
      setLoading(false);
    };
    fetchData();
  }, [showSnackbar]);

  /* ── Build the 7 column date strings for the current week ── */
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(weekSunday, i);
    return { dateStr: toDateStr(d), label: DAY_NAMES[i], date: d };
  });

  /* ── Filter logic ── */
  const filtered = schedules.filter((s) => {
    if (soloFilter && s.solo !== soloFilter) return false;
    if (searchQuery && !s.driverName?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    const ws = s.weekStart ? new Date(s.weekStart + "T00:00:00") : null;
    if (!ws) return false;
    const { start, end } = getWeekBounds(weekSunday);
    return ws >= start && ws <= end;
  });

  /* ── Week nav ── */
  const shiftWeek = (dir) => {
    setFilterMode("week_nav");
    setWeekSunday((prev) => addDays(prev, dir * 7));
  };
  const goThisWeek = () => {
    setWeekSunday(getThisWeekSunday());
    setFilterMode("this_week");
  };

  const isThisWeek =
    filterMode === "this_week" ||
    (filterMode === "week_nav" && toDateStr(weekSunday) === toDateStr(getThisWeekSunday()));

  /* ── Today's date string for highlighting ── */
  const todayStr = toDateStr(new Date());

  /* ── For a given schedule, find which blocks cover a given dateStr ── */
  const getBlocksForDay = (schedule, dateStr) => {
    if (!schedule.blocks) return [];
    return schedule.blocks.filter((b) => {
      if (!b.startDate) return false;
      const start = b.startDate;
      const end = b.endDate || b.startDate;
      return dateStr >= start && dateStr <= end;
    });
  };

  /* ── Standard copy helper (Solo 1 & 5 → +7 days) ── */
  const copyRecordsToNextWeek = async (recordsToCopy, shiftDays = 7) => {
    const newDocs = [];
    for (const s of recordsToCopy) {
      const { id: _id, createdAt: _ca, weekStart: _ws, weekEnd: _we, blocks, ...rest } = s;
      const nextStart = addDays(new Date(s.weekStart + "T00:00:00"), shiftDays);
      const nextStartStr = toDateStr(nextStart);
      const nextEndStr = toDateStr(addDays(nextStart, 6));
      const shiftedBlocks = (blocks || []).map((b) => ({
        ...b,
        startDate: shiftDateStr(b.startDate, shiftDays),
        endDate: shiftDateStr(b.endDate, shiftDays),
      }));
      const newRecord = {
        ...rest,
        weekStart: nextStartStr,
        weekEnd: nextEndStr,
        blocks: shiftedBlocks,
        createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, "schedules"), newRecord);
      newDocs.push({ id: docRef.id, ...newRecord, createdAt: new Date() });
    }
    return newDocs;
  };

  /* ── Copy Solo to Next Week ── */
  const handleCopyToNextWeek = async () => {
    if (!soloFilter) return;
    setCopying(true);
    try {
      if (soloFilter === "2") {
        // Must be week 2 to copy
        if (!solo2IsWeek2) {
          showSnackbar("Switch to SOLO 2's 2nd week to copy both weeks.", "error");
          setCopying(false);
          return;
        }
        // Gather week 2 (current) and week 1 (previous) records
        const prevSunday = addDays(weekSunday, -7);
        const { start: prevStart, end: prevEnd } = getWeekBounds(prevSunday);
        const { start: curStart, end: curEnd } = getWeekBounds(weekSunday);
        const week1Records = schedules.filter((s) => {
          if (s.solo !== "2") return false;
          const ws = s.weekStart ? new Date(s.weekStart + "T00:00:00") : null;
          return ws && ws >= prevStart && ws <= prevEnd;
        });
        const week2Records = schedules.filter((s) => {
          if (s.solo !== "2") return false;
          const ws = s.weekStart ? new Date(s.weekStart + "T00:00:00") : null;
          return ws && ws >= curStart && ws <= curEnd;
        });
        if (week1Records.length === 0 && week2Records.length === 0) {
          showSnackbar("No SOLO 2 records found for these 2 weeks.", "error");
          setCopying(false);
          return;
        }
        // Shift both weeks by 14 days
        const newDocs = await copyRecordsToNextWeek([...week1Records, ...week2Records], 14);
        setSchedules((prev) => [...newDocs, ...prev]);
        showSnackbar(
          `${week1Records.length + week2Records.length} SOLO 2 record(s) copied to next 2 weeks!`
        );
      } else {
        // SOLO 1 or SOLO 5 — standard 7-day copy
        const toCopy = filtered.filter((s) => s.solo === soloFilter);
        if (toCopy.length === 0) {
          showSnackbar(`No SOLO ${soloFilter} records found for this week.`, "error");
          setCopying(false);
          return;
        }
        const newDocs = await copyRecordsToNextWeek(toCopy, 7);
        setSchedules((prev) => [...newDocs, ...prev]);
        showSnackbar(
          `${toCopy.length} SOLO ${soloFilter} record${toCopy.length > 1 ? "s" : ""} copied to next week!`
        );
      }
    } catch (e) {
      console.error("Copy error:", e);
      showSnackbar("Failed to copy records.", "error");
    }
    setCopying(false);
  };

  /* ── Copy All to Next Week ── */
  const handleCopyAllToNextWeek = async () => {
    setCopyingAll(true);
    try {
      const { start, end } = getWeekBounds(weekSunday);

      // SOLO 1 & 5 — always copy this week, +7 days
      const solo15Records = schedules.filter((s) => {
        if (s.solo !== "1" && s.solo !== "5") return false;
        const ws = s.weekStart ? new Date(s.weekStart + "T00:00:00") : null;
        return ws && ws >= start && ws <= end;
      });

      const newDocs = [];

      if (solo15Records.length > 0) {
        const docs = await copyRecordsToNextWeek(solo15Records, 7);
        newDocs.push(...docs);
      }

      // SOLO 2 — only copy if current week is week 2
      if (solo2IsWeek2) {
        const prevSunday = addDays(weekSunday, -7);
        const { start: prevStart, end: prevEnd } = getWeekBounds(prevSunday);
        const week1Records = schedules.filter((s) => {
          if (s.solo !== "2") return false;
          const ws = s.weekStart ? new Date(s.weekStart + "T00:00:00") : null;
          return ws && ws >= prevStart && ws <= prevEnd;
        });
        const week2Records = schedules.filter((s) => {
          if (s.solo !== "2") return false;
          const ws = s.weekStart ? new Date(s.weekStart + "T00:00:00") : null;
          return ws && ws >= start && ws <= end;
        });
        if (week1Records.length > 0 || week2Records.length > 0) {
          const docs = await copyRecordsToNextWeek([...week1Records, ...week2Records], 14);
          newDocs.push(...docs);
        }
      }

      if (newDocs.length === 0) {
        showSnackbar("No records found to copy (SOLO 2 is on week 1 — skipped).", "error");
        setCopyingAll(false);
        return;
      }

      setSchedules((prev) => [...newDocs, ...prev]);
      const solo2Note = solo2IsWeek2 ? " (incl. SOLO 2 × 2 weeks)" : " (SOLO 2 skipped — not week 2)";
      showSnackbar(`${newDocs.length} record(s) copied to next week${solo2Note}!`);
    } catch (e) {
      console.error("Copy all error:", e);
      showSnackbar("Failed to copy records.", "error");
    }
    setCopyingAll(false);
  };

  /* ── Detect if currentWeekSunday is SOLO 2's week 2 ─────────────── */
  const solo2IsWeek2 = (() => {
    const thisSundayStr = toDateStr(weekSunday);
    const { start, end } = getWeekBounds(weekSunday);

    // SOLO 2 records that belong to THIS week's display
    const solo2ThisWeek = schedules.filter((s) => {
      if (s.solo !== "2") return false;
      const ws = s.weekStart ? new Date(s.weekStart + "T00:00:00") : null;
      return ws && ws >= start && ws <= end;
    });

    if (solo2ThisWeek.length === 0) return false;

    // It's week 2 if NONE of this week's SOLO 2 records have a block
    // whose startDate equals this Sunday (i.e. the route did not start today)
    const anyRouteStartsThisSunday = solo2ThisWeek.some((s) =>
      (s.blocks || []).some((b) => b.startDate === thisSundayStr)
    );

    return !anyRouteStartsThisSunday;
  })();

  /* ── Edit handlers ── */
  const toggleSortByDriver = (e) => {
    e.preventDefault();
    const x = window.scrollX;
    const y = window.scrollY;
    setSortByDriver((on) => !on);
    requestAnimationFrame(() => {
      window.scrollTo(x, y);
      requestAnimationFrame(() => window.scrollTo({ left: x, top: y, behavior: "instant" }));
    });
  };

  const handleEditClick = (s) => { setEditingId(s.id); setEditData({ ...s }); setExpandedId(s.id); };
  const handleEditCancel = () => { setEditingId(null); setEditData({}); };
  const handleDeleteSchedule = async (id) => {
    if (!window.confirm("Delete this schedule? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, "schedules", id));
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      setEditingId(null);
      setExpandedId(null);
      showSnackbar("Schedule deleted.");
    } catch (e) {
      console.error("Delete error:", e);
      showSnackbar("Failed to delete schedule.", "error");
    }
    setDeletingId(null);
  };

  const handleEditSave = async (id) => {
    setSaving(true);
    try {
      const { id: _id, createdAt, ...toSave } = editData;
      await updateDoc(doc(db, "schedules", id), toSave);
      setSchedules((prev) => prev.map((s) => s.id === id ? { ...editData } : s));
      setEditingId(null);
      showSnackbar("Schedule updated successfully!");
    } catch (e) {
      console.error("Error updating schedule:", e);
      showSnackbar("Failed to update schedule.", "error");
    }
    setSaving(false);
  };

  const handleEditChange = (field, value) =>
    setEditData((d) => ({ ...d, [field]: value }));

  const handleBlockEditChange = (idx, field, value) => {
    const blocks = [...(editData.blocks || [])];
    blocks[idx] = { ...blocks[idx], [field]: value };
    setEditData((d) => ({ ...d, blocks }));
  };

  /* ── Render the content inside a day cell ── */
  const renderDayCell = (schedule, dateStr) => {
    const blocks = getBlocksForDay(schedule, dateStr);
    const soloStyle = SOLO_COLORS[schedule.solo] || {};
    const color = soloStyle.color || "#CE4735";

    if (blocks.length === 0) {
      return <span className="sch-day-empty">FREE</span>;
    }

    return (
      <div className="sch-day-blocks">
        {blocks.map((block, bi) => {
          const cell = getCellContent(schedule, block, dateStr);

          if (cell.type === "solo1-start") {
            return (
              <div key={bi} className="sch-cell-solo1">
                <span className="sch-cell-dot" style={{ background: color }} />
                <span className="sch-cell-time" style={{ color }}>
                  {cell.time || <span className="sch-cell-notime">Scheduled</span>}
                </span>
              </div>
            );
          }

          if (cell.type === "range-start") {
            return (
              <div key={bi} className="sch-cell-range-start">
                {/* Left border acts as the "entry" indicator */}
                <span
                  className="sch-cell-range-bar sch-cell-range-bar-start"
                  style={{ background: color }}
                />
                <div className="sch-cell-range-content">
                  <span className="sch-cell-range-label" style={{ color }}>
                    Start
                  </span>
                  <span className="sch-cell-time" style={{ color }}>
                    {cell.time || "—"}
                  </span>
                </div>
              </div>
            );
          }

          if (cell.type === "range-end") {
            return (
              <div key={bi} className="sch-cell-range-end">
                <span
                  className="sch-cell-range-bar sch-cell-range-bar-end"
                  style={{ background: color }}
                />
                <div className="sch-cell-range-content">
                  <span className="sch-cell-range-label sch-cell-range-label-end">
                    END
                  </span>
                </div>
              </div>
            );
          }

          if (cell.type === "range-mid") {
            return (
              <div key={bi} className="sch-cell-range-mid">
                <span
                  className="sch-cell-range-bar sch-cell-range-bar-mid"
                  style={{ background: color }}
                />
              </div>
            );
          }

          return <span key={bi} className="sch-day-empty">—</span>;
        })}
      </div>
    );
  };

  return (
    <Layout activePage="weekly-schedules">
      <div className="sch-page">
        <Snackbar {...snackbar} />

        {/* ── Header ── */}
        <div className="sch-page-header">
          <h1 className="sch-page-title">Schedules</h1>
          <div className="sch-top-controls">
            <div className="sch-search-container">
              <i className="fas fa-search sch-search-icon"></i>
              <input
                type="text"
                placeholder="Search driver..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="sch-search-bar"
              />
            </div>
          </div>
        </div>

        {/* ── Filter Bar ── */}
        <div className="sch-filter-bar">
          <div className="sch-mode-tabs">
            {[
              { key: "this_week", label: "This Week", icon: "fa-calendar-day" },
              { key: "week_nav", label: "Browse Weeks", icon: "fa-calendar-week" },
            ].map((m) => (
              <button
                key={m.key}
                className={`sch-mode-tab ${(m.key === "this_week" && isThisWeek) ||
                  (m.key === "week_nav" && !isThisWeek)
                  ? "sch-mode-active" : ""
                  }`}
                onClick={() => {
                  setFilterMode(m.key);
                  if (m.key === "this_week") goThisWeek();
                }}
              >
                <i className={`fas ${m.icon}`}></i> {m.label}
              </button>
            ))}
          </div>

          <div className="sch-week-controls-row">
            <div className="sch-week-nav">
              <button className="sch-week-btn" onClick={() => shiftWeek(-1)} title="Previous week">
                <i className="fas fa-chevron-left"></i>
              </button>
              <span className="sch-week-label">
                {isThisWeek && <span className="sch-this-week-badge">This Week</span>}
                {fmtWeekLabel(weekSunday)}
              </span>
              <button className="sch-week-btn" onClick={() => shiftWeek(1)} title="Next week">
                <i className="fas fa-chevron-right"></i>
              </button>
              {!isThisWeek && (
                <button className="sch-today-btn" onClick={goThisWeek}>
                  <i className="fas fa-undo"></i> Today
                </button>
              )}
            </div>

            <div className="sch-week-controls-spacer" />

            <div className="sch-solo-filter-wrap">
              <label className="sch-solo-filter-label">
                <i className="fas fa-layer-group"></i> Solo
              </label>
              <select
                className="sch-solo-select"
                value={soloFilter}
                onChange={(e) => setSoloFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="1">SOLO 1</option>
                <option value="2">SOLO 2</option>
                <option value="5">SOLO 5</option>
              </select>
            </div>

            <button
              className="sch-copy-btn"
              style={soloFilter
                ? soloCopyBtnStyle(soloFilter, copyBtnHover)
                : (copyBtnHover ? COPY_ALL_STYLE.hover : COPY_ALL_STYLE.base)
              }
              onMouseEnter={() => setCopyBtnHover(true)}
              onMouseLeave={() => setCopyBtnHover(false)}
              onClick={soloFilter ? handleCopyToNextWeek : handleCopyAllToNextWeek}
              disabled={copying || copyingAll || (soloFilter === "2" && !solo2IsWeek2)}
              title={
                soloFilter === "2"
                  ? solo2IsWeek2
                    ? "Copy SOLO 2 (weeks 1 & 2) to next 2 weeks"
                    : "Navigate to SOLO 2's 2nd week to enable copy"
                  : soloFilter
                    ? `Copy all SOLO ${soloFilter} records to next week`
                    : "Copy all records for this week to next week"
              }
            >
              {(copying || copyingAll)
                ? <><i className="fas fa-spinner fa-spin"></i> Copying...</>
                : soloFilter === "2"
                  ? solo2IsWeek2
                    ? <><i className="fas fa-copy"></i> Copy SOLO 2 (2 Weeks) to Next Cycle</>
                    : <><i className="fas fa-lock"></i> SOLO 2 — Navigate to Week 2</>
                  : soloFilter
                    ? <><i className="fas fa-copy"></i> Copy SOLO {soloFilter} to Next Week</>
                    : <><i className="fas fa-copy"></i> Copy All to Next Week</>
              }
            </button>

            {!loading && (
              <span className="sch-result-count">
                {filtered.length} record{filtered.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        {/* ── Table / Records ── */}
        {loading ? (
          <div className="sch-loading">
            <i className="fas fa-spinner fa-spin"></i> Loading schedules...
          </div>
        ) : filtered.length === 0 ? (
          <div className="sch-empty">
            <i className="fas fa-mug-hot"></i>
            <p>Quiet week — nobody's on the board yet.</p>
          </div>
        ) : (
          <div className="sch-solo-groups">
            {["1", "2", "5"]
              .filter((soloKey) => !soloFilter || soloFilter === soloKey)
              .map((soloKey) => {
                const rows = filtered
                  .filter((s) => String(s.solo) === soloKey)
                  .sort((a, b) =>
                    sortByDriver
                      ? compareByDriverName(a, b)
                      : compareByWeekTimes(a, b, weekDays)
                  );
                const headingStyle = SOLO_COLORS[soloKey] || {};
                return (
                  <div key={soloKey} className="sch-solo-section">
                    <h2 className="sch-solo-heading" style={{ color: headingStyle.color || "#212121" }}>
                      <span
                        className="sch-solo-heading-bar"
                        style={{ background: headingStyle.color || "#CE4735" }}
                      />
                      SOLO {soloKey}
                      <span
                        className="sch-solo-count"
                        style={{
                          background: headingStyle.bg,
                          color: headingStyle.color,
                          border: `1px solid ${headingStyle.border || "transparent"}`,
                        }}
                      >
                        Actives: {rows.length}
                      </span>
                    </h2>
                    {rows.length === 0 ? (
                      <div className="sch-solo-section-empty">😴 This group's off duty — no drivers here.</div>
                    ) : (
          <div className="sch-table-wrapper">
            <table className="sch-table">
              <thead>
                <tr>
                  <th
                    className={`sch-th sch-th-driver ${sortByDriver ? "sch-th-sort-active" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={toggleSortByDriver}
                    title={sortByDriver ? "A–Z on. Click to sort by time." : "Click to sort A–Z by driver"}
                  >
                    Driver
                    <i className={`fas ${sortByDriver ? "fa-sort-alpha-down" : "fa-clock"}`}></i>
                  </th>
                  {weekDays.map(({ dateStr, label, date }) => (
                    <th
                      key={dateStr}
                      className={`sch-th sch-th-day ${dateStr === todayStr ? "sch-th-today" : ""}`}
                    >
                      <span className="sch-th-day-name">{label}</span>
                      <span className="sch-th-day-date">
                        {date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                      {dateStr === todayStr && <span className="sch-today-dot" />}
                    </th>
                  ))}
                  <th className="sch-th sch-th-truck">Truck</th>
                  <th className="sch-th sch-th-actions"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const isExpanded = expandedId === s.id;
                  const isEditing = editingId === s.id;
                  const soloStyle = SOLO_COLORS[s.solo] || {};
                  const current = isEditing ? editData : s;
                  const hasNotes = !!(s.notes && s.notes.trim());

                  return (
                    <React.Fragment key={s.id}>
                      {/* ── Main data row ── */}
                      <tr
                        className={`sch-tr ${isExpanded ? "sch-tr-expanded" : ""}`}
                        onClick={() => !isEditing && setExpandedId(isExpanded ? null : s.id)}
                      >
                        {/* Driver cell */}
                        <td className="sch-td sch-td-driver">
                          <div className="sch-driver-cell">
                            <div
                              className="sch-avatar-sm"
                              style={{ background: soloStyle.color || "#CE4735" }}
                            >
                              {s.driverName?.charAt(0)}
                            </div>
                            <div className="sch-driver-info">
                              <span className="sch-driver-name-tbl">
                                {s.driverName}
                                {hasNotes && (
                                  <span className="sch-note-icon" title={s.notes}>
                                    <i className="fas fa-sticky-note"></i>
                                  </span>
                                )}
                              </span>
                              <span
                                className="sch-solo-tag-sm"
                                style={{
                                  background: soloStyle.bg,
                                  color: soloStyle.color,
                                  border: `1px solid ${soloStyle.border || "transparent"}`,
                                }}
                              >
                                SOLO {s.solo}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Day cells — using renderDayCell */}
                        {weekDays.map(({ dateStr }) => {
                          const blocks = getBlocksForDay(s, dateStr);
                          const isToday = dateStr === todayStr;
                          return (
                            <td
                              key={dateStr}
                              className={`sch-td sch-td-day ${isToday ? "sch-td-today" : ""} ${blocks.length > 0 ? "sch-td-active" : ""}`}
                            >
                              {renderDayCell(s, dateStr)}
                            </td>
                          );
                        })}

                        {/* Truck cell */}
                        <td className="sch-td sch-td-truck" onClick={(e) => e.stopPropagation()}>
                          {s.truckLabel ? (
                            <span
                              className="sch-truck-badge"
                              style={{
                                background: soloStyle.bg || "rgba(142,142,147,0.1)",
                                color: soloStyle.color || "#555",
                                border: `1px solid ${soloStyle.border || "rgba(142,142,147,0.25)"}`,
                              }}
                            >
                              <i className="fas fa-truck"></i>
                              {s.truckLabel}
                            </span>
                          ) : (
                            <span className="sch-day-empty">—</span>
                          )}
                        </td>

                        {/* Actions cell */}
                        <td className="sch-td sch-td-actions" onClick={(e) => e.stopPropagation()}>
                          <div className="sch-row-actions">
                            {!isEditing && (
                              <button
                                className="sch-edit-btn"
                                onClick={(e) => { e.stopPropagation(); handleEditClick(s); }}
                              >
                                <i className="fas fa-pen"></i> Edit
                              </button>
                            )}
                            <button
                              className={`sch-expand-btn ${isExpanded ? "sch-expand-active" : ""}`}
                              onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : s.id); }}
                              title={isExpanded ? "Collapse" : "Show details"}
                            >
                              <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}></i>
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* ── Expanded detail row ── */}
                      {isExpanded && (
                        <tr className="sch-tr-detail">
                          <td colSpan={10} className="sch-td-detail">
                            <div className="sch-detail-panel">

                              {/* Contact details */}
                              <div className="sch-detail-row">
                                <div className="sch-detail-item">
                                  <span>Email</span><strong>{s.email || "—"}</strong>
                                </div>
                                <div className="sch-detail-item">
                                  <span>Phone</span><strong>{s.phone || "—"}</strong>
                                </div>
                                <div className="sch-detail-item">
                                  <span>Fuel Card</span><strong>{s.fuelCard || "—"}</strong>
                                </div>
                                {s.truckLabel && (
                                  <div className="sch-detail-item">
                                    <span>Truck</span><strong>{s.truckLabel}</strong>
                                  </div>
                                )}
                                <div className="sch-detail-item">
                                  <span>Week</span>
                                  <strong>{fmtDate(s.weekStart, true)} – {fmtDate(s.weekEnd, true)}</strong>
                                </div>
                              </div>

                              {/* Blocks table (editing mode) */}
                              {isEditing && (
                                <div className="sch-blocks-table">
                                  <div className="sch-blocks-head">
                                    <span>#</span>
                                    <span>Start Date</span>
                                    <span>End Date</span>
                                    <span>Start Time</span>
                                  </div>
                                  {(current.blocks || []).map((block, idx) => (
                                    <div key={idx} className="sch-blocks-row sch-blocks-editing">
                                      <span className="sch-block-idx">{idx + 1}</span>
                                      <input type="date" className="sch-inline-input"
                                        value={block.startDate || ""}
                                        onChange={(e) => handleBlockEditChange(idx, "startDate", e.target.value)} />
                                      <input type="date" className="sch-inline-input"
                                        value={block.endDate || ""}
                                        onChange={(e) => handleBlockEditChange(idx, "endDate", e.target.value)} />
                                      <TimePicker24
                                        value={block.startTime || ""}
                                        onChange={(val) => handleBlockEditChange(idx, "startTime", val)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Notes */}
                              <div className="sch-notes-row">
                                <span className="sch-notes-label">
                                  <i className="fas fa-sticky-note"></i> Notes
                                </span>
                                {isEditing ? (
                                  <textarea
                                    className="sch-notes-input"
                                    value={editData.notes || ""}
                                    onChange={(e) => handleEditChange("notes", e.target.value)}
                                    rows={3}
                                  />
                                ) : (
                                  <p className="sch-notes-text">{s.notes?.trim() ? s.notes : "-"}</p>
                                )}
                              </div>

                              {isEditing && (
                                <div className="sch-edit-actions">
                                  <button
                                    className="sch-btn-delete"
                                    onClick={() => handleDeleteSchedule(s.id)}
                                    disabled={saving || deletingId === s.id}
                                    title="Delete this schedule"
                                  >
                                    {deletingId === s.id
                                      ? <><i className="fas fa-spinner fa-spin"></i> Deleting...</>
                                      : <><i className="fas fa-trash-alt"></i> Delete</>
                                    }
                                  </button>
                                  <div className="sch-edit-actions-right">
                                    <button className="sch-btn-cancel" onClick={handleEditCancel} disabled={saving}>
                                      Cancel
                                    </button>
                                    <button className="sch-btn-save" onClick={() => handleEditSave(s.id)} disabled={saving}>
                                      {saving
                                        ? <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                                        : <><i className="fas fa-check"></i> Save Changes</>
                                      }
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Schedules;